import type { EmploymentTerms } from './employment';
import { italianHolidays } from './italy/calendar';
import { validWeeklySlots, type WorkSchedule } from './workSchedule';
export interface CalendarException { date: string; working: boolean; scope: 'personal' | 'site' | 'company'; scopeId?: string | null }
export interface CalendarInterval { start: number; end: number; scheduleId: string; anchorDate: string }
export interface CalendarDay { date: string; minutes: number | null; intervals: CalendarInterval[]; source: 'pattern' | 'national' | CalendarException['scope']; incomplete: boolean }
const DAY=86400000;
function civil(date:string):number {
 if(!/^\d{4}-\d{2}-\d{2}$/.test(date))throw new Error('calendar_invalid_date');
 const ms=Date.parse(`${date}T00:00:00Z`);
 if(!Number.isFinite(ms)||new Date(ms).toISOString().slice(0,10)!==date||date<'1900-01-01'||date>'2100-12-31')throw new Error('calendar_invalid_date');
 return ms;
}
/** Pure wall-minute projection. No punches, payroll deductions or conversions to UTC instants. */
export function workCalendar(from:string,to:string,schedules:WorkSchedule[],exceptions:CalendarException[]=[],terms?:EmploymentTerms[]):CalendarDay[] {
 const first=civil(from),last=civil(to);
 if(last<first||last-first>366*DAY)throw new Error('calendar_invalid_range');
 for(const schedule of schedules){civil(schedule.effective_from);if(!validWeeklySlots(schedule.slots))throw new Error('schedule_invalid');}
 const overrides=new Map<string,CalendarException[]>();
 const priorities={company:1,site:2,personal:3};
 const seen=new Set<string>();
 for(const override of exceptions){
  civil(override.date);
  if(typeof override.working!=='boolean'||!['personal','site','company'].includes(override.scope))throw new Error('calendar_invalid_exception');
  if(override.scope==='site'&&!override.scopeId)throw new Error('calendar_invalid_exception');
  const key=`${override.scope}:${override.scopeId??''}:${override.date}`;
  if(seen.has(key))throw new Error('calendar_duplicate_exception');seen.add(key);
  const current=overrides.get(override.date)??[];
  overrides.set(override.date,[...current,override]);
 }
 const versions=schedules.filter(x=>!x.voided_at).sort((a,b)=>b.effective_from.localeCompare(a.effective_from));
 if(new Set(versions.map(x=>x.effective_from)).size!==versions.length)throw new Error('schedule_ambiguous');
 const holidayCache=new Map<number,Set<string>>();
 function rule(date:string):{working:boolean;source:CalendarDay['source']} {
  const override=(overrides.get(date)??[]).filter(x=>x.scope!=='site'||x.scopeId===at(date)?.site_id).sort((a,b)=>priorities[b.scope]-priorities[a.scope])[0];
  if(override)return {working:override.working,source:override.scope};
  const year=Number(date.slice(0,4));
  if(!holidayCache.has(year))holidayCache.set(year,new Set(italianHolidays(year)));
  return holidayCache.get(year)!.has(date)?{working:false,source:'national'}:{working:true,source:'pattern'};
 }
 function at(date:string){return versions.find(x=>x.effective_from<=date);}
 const result:CalendarDay[]=[];
 for(let ms=first;ms<=last;ms+=DAY){
  const date=new Date(ms).toISOString().slice(0,10),previous=new Date(ms-DAY).toISOString().slice(0,10);
  const current=at(date),before=at(previous),todayRule=rule(date);
  const intervals:CalendarInterval[]=[];
  // Each shift follows its starting-day rule. A previous-day shift is split at midnight, not dropped or counted twice.
  for(const [anchor,version,carry] of [[previous,before,true],[date,current,false]] as const){
   if(!version||!rule(anchor).working)continue;
   const weekday=((new Date(`${anchor}T00:00:00Z`).getUTCDay()+6)%7)+1;
   for(const slot of version.slots.filter(x=>x.day===weekday)){
    const start=carry?0:slot.start,end=carry?slot.next_day?slot.end:0:slot.next_day?1440:slot.end;
    if(end>start)intervals.push({start,end,scheduleId:version.id,anchorDate:anchor});
   }
  }
  intervals.sort((a,b)=>a.start-b.start);
  if(intervals.some((slot,i)=>i>0&&slot.start<intervals[i-1]!.end))throw new Error('calendar_boundary_overlap');
  // Unknown prior-day pattern can hide an overnight carry, so never claim a complete numeric result.
  const termAt=(date:string)=>terms?.filter(x=>!x.voided_at&&x.effective_from<=date).sort((a,b)=>b.effective_from.localeCompare(a.effective_from))[0];
  const incompatible=terms!==undefined&&(!current||termAt(date)?.id!==current.employment_terms_id||Boolean(before&&termAt(previous)?.id!==before.employment_terms_id));
  const incomplete=!current||!before||incompatible;
  result.push({date,intervals,source:todayRule.source,incomplete,minutes:incomplete?null:intervals.reduce((sum,x)=>sum+x.end-x.start,0)});
 }
 return result;
}

export function calendarMonth(month:string,schedules:WorkSchedule[],terms:EmploymentTerms[],exceptions:CalendarException[]=[]):CalendarDay[] {
 if(!/^\d{4}-(0[1-9]|1[0-2])$/.test(month))throw new Error('calendar_invalid_date');
 const [year,m]=month.split('-').map(Number);
 const last=new Date(Date.UTC(year!,m!,0)).toISOString().slice(0,10);
 return workCalendar(`${month}-01`,last,schedules,exceptions,terms);
}
export function shiftCalendarMonth(month:string,delta:number):string {
 if(!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)||!Number.isInteger(delta)||Math.abs(delta)>1200)throw new Error('calendar_invalid_date');
 const [year,m]=month.split('-').map(Number);
 const next=new Date(Date.UTC(year!,m!-1+delta,1)).toISOString().slice(0,7);
 civil(`${next}-01`);return next;
}

export interface CalendarEntry {
 id:string;company_id:string;member_id:string|null;site_id:string|null;date:string;working:boolean;
 kind:'patron'|'closure'|'workday';voided_at:string|null;
}
export function calendarOverrides(rows:CalendarEntry[]):CalendarException[] {
 return rows.filter(x=>!x.voided_at).map(x=>({date:x.date,working:x.working,scope:x.member_id?'personal':x.site_id?'site':'company',scopeId:x.member_id??x.site_id}));
}
